import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';

import EnhancedTableHead from '../../components/EnhancedTableHead';
import SimpleTableToolbar from '../../components/SimpleTableToolbar';
import EnhancedTableRow from '../../components/EnhancedTableRow';
import { getComparator, stableSort } from '../../modules/sort';
import SearchBox from '../../components/SearchBox';
import ExpandButton from '../../components/ExpandButton';
import NewProfileView from './NewProfileView';
import { useFirestoreDataAndMapping } from '../../modules/uitls';

function NewProfileDetail({ userMapping, newProfile, productMapping }) {
  const { content, ...p } = newProfile
  let fieldName = ''
  if (newProfile.profile === 'supplier') {
    fieldName = 'supplierId'
  } else if (newProfile.profile === 'merchandise') {
    fieldName = 'merchandiseId'
  } else if (['product', 'package', 'combination', 'folder'].includes(newProfile.profile)) {
    fieldName = 'productId'
  }

  const r = { ...p, ...content, [fieldName]: newProfile[fieldName] ? true : false }
  r.createdBy = { id: r.createdBy, name: userMapping[r.createdBy]?.displayName }
  return (
    <div style={{ padding: 15 }}>
      <NewProfileView userMapping={userMapping} content={r} productMapping={productMapping} />
    </div>
  )
}

NewProfileDetail.propTypes = {
  newProfile: PropTypes.object.isRequired,
  userMapping: PropTypes.object.isRequired,
  productMapping: PropTypes.object
};

function NewProfileList({ newProfiles, title, productMapping }) {
  const { formatMessage } = useIntl()

  const [userMapping] = useFirestoreDataAndMapping('users')
  const [customerMapping] = useFirestoreDataAndMapping('customers')

  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('sn')
  const [currentFilter, setCurrentFilter] = useState(null)
  const [expand, setExpand] = useState(true);
  const filteredNewProfiles = currentFilter && currentFilter.text ? filterByText() : newProfiles

  const headerCells = [
    { text: 'sn', sort: 'sn' },
    { text: 'sourceName', sort: 'sourceName' },
    { text: 'createdBy', sort: 'createdBy' },
    { text: 'date', sort: 'createdAt' },
  ].map(c => {c.text = formatMessage({ id: `newProfile.table.header.${c.text}` });return c})

  const rowCells = [
    { field: 'sn' },
    { field: 'sourceName' },
    { field: 'createdBy' },
    { field: 'date' },
  ]

  function filterByText() {
    if (currentFilter.name === 'sourceName') {
      return newProfiles.filter(s => customerMapping[s.source]?.nickname.toLowerCase().includes(currentFilter.text.toLowerCase()))
    } else if (currentFilter.name === 'createdBy') {
      return newProfiles.filter(s => userMapping[s.createdBy]?.displayName.toLowerCase().includes(currentFilter.text.toLowerCase()))
    } else {
      return newProfiles.filter(s => s[currentFilter.name].toLowerCase().includes(currentFilter.text.toLowerCase()))
    }
  }

  const formatData = (newProfile) => {
    const newData = { ...newProfile }
    newData.sourceName = customerMapping[newData.source]?.nickname
    newData.createdBy = userMapping[newData.createdBy]?.displayName
    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filterItems = [
    { name: 'sn' },
    { name: 'sourceName', type: 'customer' },
    { name: 'createdBy' },
    { name: 'date' },
    { name: 'note' },
  ].map(i => {i.text = formatMessage({ id: `newProfile.table.detail.${i.name}` });return i})

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  return (
    <div>
      <SimpleTableToolbar
        title={title}
        buttons={<ExpandButton open={expand} onExpandChange={setExpand} />}
        toolbox={<SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} />}
      />
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: '650px' }}>
            <EnhancedTableHead
              headerCells={headerCells}
              order={order}
              orderBy={orderBy}
              expandable
              onRequestSort={handleRequestSort}
              rowCount={filteredNewProfiles.length}
            />
            <TableBody>
              {stableSort(filteredNewProfiles.map(r => formatData(r)), getComparator(order, orderBy)).map(newProfile => (
                <EnhancedTableRow
                  key={newProfile.id}
                  rowCells={rowCells}
                  cellData={newProfile}
                  expandable
                  expandContent={<NewProfileDetail userMapping={userMapping} newProfile={newProfile} productMapping={productMapping} />}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </div>
  );
}

NewProfileList.propTypes = {
  newProfiles: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  title: PropTypes.string.isRequired,
  productMapping: PropTypes.object
};

export default NewProfileList;
